"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.POSListings = void 0;
const products_1 = require("@getgreenline/products");
class POSListingProductRelationship {
    constructor() {
        /** Relationship leger containing product ids and their parent product & ref data ids */
        this.relationshipLookup = new Map();
        /** Category leger containing product ids and their category ids */
        this.categoryLookup = new Map();
        /** Get a relationship */
        this.getRelationShip = (productId) => this.relationshipLookup.get(productId);
        /** Get all product ids by category id */
        this.getProductIdsByCategoryId = (categoryId) => {
            const productMap = this.categoryLookup.get(categoryId);
            return productMap ? [...productMap.keys()] : [];
        };
        /** Remove all relationship legers */
        this.clearAllRelationships = () => {
            this.relationshipLookup.clear();
            this.categoryLookup.clear();
        };
        /** Set a relationship */
        this.setRelationship = (product) => {
            this.setProductIdInCategory(product.categoryId, product.id);
            this.relationshipLookup.set(product.id, {
                parentProductId: product.parentProductId,
                categoryId: product.categoryId,
            });
        };
        /** Delete a relationship */
        this.deleteRelationship = (product) => {
            const relationship = this.getRelationShip(product.id);
            if (relationship === null || relationship === void 0 ? void 0 : relationship.categoryId) {
                this.deleteProductIdFromCategory(relationship.categoryId, product.id);
            }
            this.relationshipLookup.delete(product.id);
        };
        /** Set a relationship between product id and category id */
        this.setProductIdInCategory = (categoryId, productId) => {
            const relationship = this.getRelationShip(productId);
            if (relationship) {
                const prevCategoryId = relationship.categoryId;
                if (prevCategoryId !== categoryId) {
                    // Category id changed. Remove product from previous category id
                    this.deleteProductIdFromCategory(prevCategoryId, productId);
                }
            }
            const productMap = this.categoryLookup.get(categoryId) || new Map();
            productMap.set(productId, null);
            this.categoryLookup.set(categoryId, productMap);
        };
        // Delete product id from the category leger
        this.deleteProductIdFromCategory = (categoryId, productId) => {
            var _a;
            (_a = this.categoryLookup.get(categoryId)) === null || _a === void 0 ? void 0 : _a.delete(productId);
        };
    }
}
class POSListingBatches {
    constructor() {
        /** Batch leger */
        this.batchLookup = new Map();
        /** Reset all mapped batches */
        this.setFreshAll = (batches) => {
            this.batchLookup.clear();
            this.updateAll(batches);
        };
        /** Update existing mapped batches */
        this.updateAll = (batches) => {
            batches.forEach((batch) => {
                batch.status === products_1.BatchModels.BatchStatus.CLOSED ? this.delete(batch) : this.update(batch);
            });
        };
        /** Get a batch by product id and batch id */
        this.get = (productId, batchId) => {
            var _a;
            const batch = (_a = this.batchLookup.get(productId)) === null || _a === void 0 ? void 0 : _a.get(batchId);
            if (!batch)
                return;
            return {
                id: batch.id,
                batchNumber: batch.batchNumber,
                quantityRemaining: batch.quantity,
                cannabinoidMetaData: batch.cannabinoidMetaData,
            };
        };
        /** Get all batches that belong to a product */
        this.getAllPerProduct = (productId) => {
            const batchMap = this.batchLookup.get(productId);
            if (!batchMap)
                return [];
            const batches = [];
            for (const b of batchMap.values()) {
                const batch = this.get(productId, b.id);
                if (batch)
                    batches.push(batch);
            }
            return batches;
        };
        /** Delete all batches that belong to a product */
        this.deleteAllPerProduct = (productId) => {
            var _a;
            (_a = this.batchLookup.get(productId)) === null || _a === void 0 ? void 0 : _a.clear();
        };
        /** Delete a batch */
        this.delete = (batch) => {
            var _a;
            (_a = this.batchLookup.get(batch.productId)) === null || _a === void 0 ? void 0 : _a.delete(batch.id);
        };
        /** Update a batch */
        this.update = (batch) => {
            const existingBatchMap = this.batchLookup.get(batch.productId);
            const batchMap = existingBatchMap || new Map();
            batchMap.set(batch.id, batch);
            this.batchLookup.set(batch.productId, batchMap);
        };
    }
    /** Get all batch records */
    get all() {
        const mappedBatches = [...this.batchLookup.values()];
        return mappedBatches.reduce((acc, curr) => [...acc, ...[...curr.values()]], []);
    }
}
class POSListingProducts extends POSListingProductRelationship {
    constructor(POSListings) {
        super();
        this.POSListings = POSListings;
        /** Standalone or master product leger */
        this.parentProductLookup = new Map();
        /** Child product leger */
        this.childProductLookup = new Map();
        this.batches = new POSListingBatches();
        /** Reset all mapped products */
        this.setFreshAll = (products) => {
            this.clearAllRelationships();
            this.parentProductLookup.clear();
            this.childProductLookup.clear();
            this.updateAll(products);
        };
        /** Update existing mapped products */
        this.updateAll = (products) => {
            products.forEach((product) => {
                product.isDeleted || product.isActive === false ? this.delete(product) : this.update(product);
            });
            this.POSListings.updateFlattenedListings();
        };
        /** Get a standalone or parent product */
        this.getTopLevel = (productId) => this.parentProductLookup.get(productId);
        /** Get child products that belong to a master product */
        this.getChildren = (parentProductId) => {
            const mappedChildProducts = this.childProductLookup.get(parentProductId);
            return mappedChildProducts ? [...mappedChildProducts.values()] : [];
        };
        /** Get a child product */
        this.getChild = (parentProductId, childProductId) => {
            var _a;
            return (_a = this.childProductLookup.get(parentProductId)) === null || _a === void 0 ? void 0 : _a.get(childProductId);
        };
        /** Update a product and its master-child relationship */
        this.update = (product) => {
            const prevRelationship = this.getRelationShip(product.id);
            if (!prevRelationship) {
                // New product addition
                this.add(product);
            }
            else if (prevRelationship.parentProductId === null && product.parentProductId !== null) {
                // Standalone merged as a child product under another parent product
                this.parentProductLookup.delete(product.id);
                this.setChild(product.parentProductId, product);
            }
            else if (prevRelationship.parentProductId !== null && product.parentProductId === null) {
                // Child product split as a standalone product
                this.deleteChild(prevRelationship.parentProductId, product);
                this.parentProductLookup.set(product.id, product);
            }
            else if (prevRelationship.parentProductId !== null &&
                product.parentProductId !== null &&
                prevRelationship.parentProductId !== product.parentProductId) {
                // Child product split to / merged as variant
                this.deleteChild(prevRelationship.parentProductId, product);
                this.setChild(product.parentProductId, product);
            }
            else {
                this.add(product);
            }
            this.setRelationship(product);
        };
        /** Add a product */
        this.add = (product) => {
            if (product.parentProductId === null) {
                // Parent product
                this.parentProductLookup.set(product.id, product);
            }
            else {
                // Child product
                this.setChild(product.parentProductId, product);
            }
        };
        /** Delete a product */
        this.delete = (product) => {
            const relationship = this.getRelationShip(product.id);
            // Remove batches
            for (const p of [product, ...this.getChildren(product.id)]) {
                this.batches.deleteAllPerProduct(p.id);
            }
            if (relationship === null || relationship === void 0 ? void 0 : relationship.parentProductId) {
                // Child product status is "deleted". Remove the child product record
                this.deleteChild(relationship.parentProductId, product);
            }
            else {
                // Master product status is "deleted". Remove the parent and and all its child product records
                this.parentProductLookup.delete(product.id);
                this.childProductLookup.delete(product.id);
            }
            this.deleteRelationship(product);
        };
        /** Set a child product */
        this.setChild = (parentProductId, childProduct) => {
            const existingChildProductMap = this.childProductLookup.get(parentProductId);
            const childProductMap = existingChildProductMap || new Map();
            childProductMap.set(childProduct.id, childProduct);
            this.childProductLookup.set(parentProductId, childProductMap);
        };
        /** Delete a child product */
        this.deleteChild = (parentProductId, childProduct) => {
            var _a;
            (_a = this.childProductLookup.get(parentProductId)) === null || _a === void 0 ? void 0 : _a.delete(childProduct.id);
        };
    }
    /** Get all product records */
    get all() {
        let allProducts = [];
        this.parentProductIds.forEach((parentProductId) => {
            const topLevelProduct = this.getTopLevel(parentProductId);
            const childProducts = this.getChildren(parentProductId);
            if (topLevelProduct) {
                allProducts = [...allProducts, topLevelProduct];
            }
            allProducts = [...allProducts, ...childProducts];
        });
        return allProducts;
    }
    /** Get all standalone or master product ids */
    get parentProductIds() {
        return [...this.parentProductLookup.keys()];
    }
}
class POSListingProductInventory {
    constructor(POSListings) {
        this.POSListings = POSListings;
        /** Product inventory leger */
        this.inventoryLookup = new Map();
        /** Update all product inventory */
        this.updateAll = (inventory, shouldUpdateFlattenedListings = true) => {
            inventory.forEach((productInventory) => {
                this.updateForProduct(productInventory);
            });
            if (shouldUpdateFlattenedListings)
                this.POSListings.updateFlattenedListings();
        };
        /** Update one product inventory */
        this.updateForProduct = (inventory) => {
            this.inventoryLookup.set(inventory.productId, inventory);
        };
        /** Get product inventory */
        this.getForProduct = (productId) => { var _a; return ((_a = this.inventoryLookup.get(productId)) === null || _a === void 0 ? void 0 : _a.quantity) || 0; };
    }
    /** Get all inventory records */
    get all() {
        return [...this.inventoryLookup.values()];
    }
}
class RefData {
    constructor() {
        /** Category leger */
        this.categoryLookup = new Map();
        /** Supplier leger */
        this.supplierLookup = new Map();
        /** Tax group leger */
        this.taxGroupLookup = new Map();
        /** Tax leger */
        this.taxLookup = new Map();
        /** Update all ref data */
        this.updateAll = ({ categories, suppliers, taxGroups, taxes, }) => {
            if (categories)
                this.updateCategories(categories);
            if (suppliers)
                this.updateSuppliers(suppliers);
            if (taxGroups)
                this.updateTaxGroups(taxGroups);
            if (taxes)
                this.updateTaxes(taxes);
        };
        /** Get a category */
        this.getCategory = (categoryId) => {
            return categoryId === null ? null : this.categoryLookup.get(categoryId) || null;
        };
        /** Get a supplier */
        this.getSupplier = (supplierId) => {
            return supplierId === null ? null : this.supplierLookup.get(supplierId) || null;
        };
        /** Get a tax group */
        this.getTaxGroup = (taxGroupId) => {
            return taxGroupId === null ? null : this.taxGroupLookup.get(taxGroupId) || null;
        };
        /** Get all child categories */
        this.getChildCategories = (categoryId) => {
            return this.categories.filter((c) => c.parentCategoryId === categoryId);
        };
        /** Get a tax */
        this.getTax = (taxId) => this.taxLookup.get(taxId) || null;
        /** Get ref data for a product */
        this.getRefData = (product) => {
            return Object.assign(Object.assign({}, this.getCategoryRefData(product.categoryId)), { taxes: this.getProductTaxes(product.taxGroupId) });
        };
        /** Update all categories */
        this.updateCategories = (categories) => {
            this.categoryLookup.clear();
            categories.forEach((category) => {
                const { childCategories } = category, parentCategory = __rest(category, ["childCategories"]);
                const flatCategories = [parentCategory, ...childCategories];
                flatCategories.forEach((category) => this.categoryLookup.set(category.id, category));
            });
        };
        /** Update all suppliers */
        this.updateSuppliers = (suppliers) => {
            this.supplierLookup.clear();
            suppliers.forEach((supplier) => this.supplierLookup.set(supplier.id, supplier));
        };
        /** Update all tax groups */
        this.updateTaxGroups = (taxGroups) => {
            this.taxGroupLookup.clear();
            taxGroups.forEach((taxGroup) => this.taxGroupLookup.set(taxGroup.id, taxGroup));
        };
        /** Update all taxes */
        this.updateTaxes = (taxes) => {
            this.taxLookup.clear();
            taxes.forEach((tax) => this.taxLookup.set(tax.id, tax));
        };
        /** Get category ref data */
        this.getCategoryRefData = (categoryId) => {
            const category = categoryId ? this.getCategory(categoryId) : null;
            return {
                complianceCategoryId: (category === null || category === void 0 ? void 0 : category.complianceCategoryId) || null,
                parentCategoryId: (category === null || category === void 0 ? void 0 : category.parentCategoryId) || null,
            };
        };
        /** Get taxes that belong to a tax group */
        this.getProductTaxes = (taxGroupId) => {
            if (taxGroupId === null)
                return [];
            const taxGroup = this.getTaxGroup(taxGroupId);
            if (!taxGroup)
                return [];
            const taxes = [];
            taxGroup.taxLines.forEach((taxLine) => {
                const targetTax = this.getTax(taxLine.taxId);
                if (targetTax)
                    taxes.push(targetTax);
            });
            return taxes;
        };
    }
    /** Get all flat categories */
    get categories() {
        return [...this.categoryLookup.values()];
    }
    /** Get all suppliers */
    get suppliers() {
        return [...this.supplierLookup.values()];
    }
}
class POSListings {
    constructor() {
        this.products = new POSListingProducts(this);
        this.productInventory = new POSListingProductInventory(this);
        this.refData = new RefData();
        /** Get child pos listings that belong to a parent pos listing, sorted by name */
        this.getChildren = (parentProductId) => {
            const childListings = [];
            this.products.getChildren(parentProductId).forEach((cp) => {
                const childListing = this.getChild(parentProductId, cp.id);
                if (childListing)
                    childListings.push(childListing);
            });
            return childListings.sort((a, b) => a.name.localeCompare(b.name));
        };
        this.updateFlattenedListings = () => {
            this.flattenedListings = this.allTopLevels.reduce((acc, curr) => {
                if (curr.isMasterProduct) {
                    const childListings = this.getChildren(curr.id);
                    return [...acc, ...childListings];
                }
                return [...acc, curr];
            }, []);
        };
        /** Get standalone or master listings by category id, sorted by name */
        this.getTopLevelsByCategoryId = (categoryId) => {
            const topLevelListings = [];
            this.products.getProductIdsByCategoryId(categoryId).forEach((productId) => {
                const topLevelListing = this.getTopLevel(productId);
                if (topLevelListing)
                    topLevelListings.push(topLevelListing);
            });
            return topLevelListings.sort((a, b) => a.name.localeCompare(b.name));
        };
        /** Get a flat pos listing by product id */
        this.getByProductId = (productId) => {
            const relationship = this.products.getRelationShip(productId);
            // id does not exist
            if (relationship === undefined)
                return;
            // Standalone or master product id
            if (relationship.parentProductId === null) {
                return this.getTopLevel(productId);
            }
            // id belongs to a child product
            return this.getChild(relationship.parentProductId, productId);
        };
        /** Get a nested pos listing by product id */
        this.getNestedByProductId = (productId) => {
            const relationship = this.products.getRelationShip(productId);
            // id does not exist
            if (relationship === undefined)
                return;
            // Standalone or master product id
            if (relationship.parentProductId === null) {
                const parentOrStandaloneListing = this.getTopLevel(productId);
                return parentOrStandaloneListing
                    ? this.getNestedByParentProductId(parentOrStandaloneListing.id)
                    : undefined;
            }
            // id belongs to a child product
            const childListing = this.getChild(relationship.parentProductId, productId);
            if (!childListing)
                return;
            return Object.assign(Object.assign({}, childListing), { childProducts: [] });
        };
        /** Hash meta data */
        this.getHashedMetaData = (metaData) => {
            const metaDataHash = {};
            metaData.forEach((item) => (metaDataHash[item.metaKey] = item.metaValue));
            return metaDataHash;
        };
        /** Get standalone or master pos listing */
        this.getTopLevel = (productId) => {
            const parentOrStandaloneProduct = this.products.getTopLevel(productId);
            if (!parentOrStandaloneProduct)
                return;
            return Object.assign(Object.assign(Object.assign({}, parentOrStandaloneProduct), this.refData.getRefData(parentOrStandaloneProduct)), { parentProductName: null, quantity: this.productInventory.getForProduct(productId), metaData: this.getHashedMetaData(parentOrStandaloneProduct.metaData) });
        };
        /** Get a child pos listing */
        this.getChild = (parentProductId, childProductId) => {
            var _a;
            const childProduct = this.products.getChild(parentProductId, childProductId);
            if (!childProduct)
                return;
            return Object.assign(Object.assign(Object.assign({}, childProduct), this.refData.getRefData(childProduct)), { parentProductName: ((_a = this.getTopLevel(parentProductId)) === null || _a === void 0 ? void 0 : _a.name) || null, quantity: this.productInventory.getForProduct(childProduct.id), metaData: this.getHashedMetaData(childProduct.metaData) });
        };
        /** Get a nested pos listing by parent product id */
        this.getNestedByParentProductId = (parentProductId) => {
            const parentProduct = this.getTopLevel(parentProductId);
            if (!parentProduct)
                return;
            return Object.assign(Object.assign({}, parentProduct), { childProducts: this.getChildren(parentProductId) });
        };
    }
    /** Get all standalone or master pos listings in a flat structure, sorted by name */
    get allTopLevels() {
        const allTopLevelListings = [];
        this.products.parentProductIds.forEach((parentproductId) => {
            const topLevelListing = this.getTopLevel(parentproductId);
            if (topLevelListing)
                allTopLevelListings.push(topLevelListing);
        });
        return allTopLevelListings.sort((a, b) => a.name.localeCompare(b.name));
    }
    /** Get all child pos listings in a flat structure */
    get allChildren() {
        return this.products.parentProductIds.reduce((acc, parentProductId) => {
            return [...acc, ...this.getChildren(parentProductId)];
        }, []);
    }
    /** Get all pos listings in a flat structure */
    get all() {
        return [...this.allTopLevels, ...this.allChildren];
    }
    /** Get all pos listings in a nested structure */
    get allNested() {
        const allNestedListings = [];
        this.products.parentProductIds.forEach((parentproductId) => {
            const nestedListing = this.getNestedByParentProductId(parentproductId);
            if (nestedListing)
                allNestedListings.push(nestedListing);
        });
        this.allChildren.forEach((child) => allNestedListings.push(Object.assign(Object.assign({}, child), { childProducts: [] })));
        return allNestedListings;
    }
}
exports.POSListings = POSListings;
//# sourceMappingURL=POSListings.js.map